import { AgentSessionCreationFailedError, AgentSessionNotFoundError, AgentSessionsFetchFailedError } from "@madhive/mad-sdk";
import { useMadSDKSingular } from "../../madSDK";
import { madSDK } from "../../../sdk";
import { useMadStorage } from "../state/madStorage";
import { useEffect } from "react";
import { useSingularMadHooksUtils } from "../../madSDK/singular/singularUtils";
// Shared placeholder used while lazy (not yet activated) — never a real id to fetch/ensure a session for.
const LAZY_SESSION_PLACEHOLDER = "lazy";
/**
 * Hook handles the retrieval and storage of an AI Agent Session ID.
 * It leverages {@link useMadStorage} to persist the session ID,
 * while providing proxy ids if the provided id is expired or not found.
 *
 * This hook is primarily an internal utility used by `useAgentSession` to manage session IDs, and rarely (if ever) used directly.
 * @param agent The name of the AI agent. {@link AgentName}
 * @param id id of the agent session
 * @param options
 * @returns {SingularAgentSessionIdData}
 */
export function useAgentSessionId(agent, id, { storage = window.sessionStorage, ttl = 24 * 60 * 60 * 1000, ensureSession = true, ...loadingOptions } = {}) {
    // Lazy mode (not yet activated) has no real id yet — resolve to a shared placeholder so the
    // key generation and parse fallback below stay consistent with each other.
    const resolvedId = id || LAZY_SESSION_PLACEHOLDER;
    const key = makeSessionKey(agent, resolvedId);
    const [storedId, setStoredId] = useMadStorage(key, {
        parse: parseAgentSessionStorageValue(resolvedId, { ensureSession }),
        stringify: stringifyAgentSessionStorageValue(ttl),
        storage: storage === false ? useAgentSessionId.noopStorage : storage,
        fallback: id
    });
    const handler = ensureSession ? madSDK.ai.agents[agent].ensuredIds : madSDK.ai.agents[agent].ids;
    // When id is undefined (lazy mode, not yet activated), pass undefined to skip fetching
    const aiSessionId = useMadSDKSingular(handler, id ? storedId : undefined, loadingOptions);
    // Getting the observer to sync storage changes
    const { observer } = useSingularMadHooksUtils(handler, storedId);
    // Sync storage when id changes.
    // Gated on `id` so this stays inert while lazy — the "lazy" bucket/placeholder is shared
    // across every not-yet-activated instance of this agent, so writing here before a real id
    // exists could leak a resolved session id from one lazy chat instance into another.
    useEffect(() => {
        if (!id) {
            return undefined;
        }
        const subscription = observer.subscribe(({ data }) => {
            if (data?.id && data.id !== storedId) {
                setStoredId(data.id);
            }
        });
        return () => {
            subscription.unsubscribe();
        };
    }, [id, observer, storedId]);
    /**
     * Creates a new session ID and updates the storage.
     * @returns {Promise<string | undefined>} The new session ID.
     */
    async function newSession() {
        try {
            const nextSession = await aiSessionId.save({ id: id || "new" });
            setStoredId(nextSession.id);
            return nextSession.id;
        }
        catch (err) {
            // Save failure is handled by useMadSDKSingular's error state, so we can ignore it here
            console.error(`Failed to create new agent session (${id})`, err);
            return undefined;
        }
    }
    /**
     * Saves data with a retry mechanism in case of session expiration or not found errors.
     * If the session ID is expired or not found, it will attempt to create a new session ID
     * and retry the save operation.
     */
    async function withEnsuredSession(fn, { onError = (failedSessionId, error) => {
        console.error(`Failed to find and create new agent session (${failedSessionId})`, error);
    } } = {}) {
        const currentSessionId = aiSessionId.data?.id;
        const retryWithNewSession = async () => {
            try {
                const newSessionId = await newSession();
                if (!newSessionId) {
                    onError(aiSessionId.data?.id || id || "unknown", new AgentSessionCreationFailedError());
                    return;
                }
                await fn(newSessionId);
            }
            catch (refreshErr) {
                onError(aiSessionId.data?.id || id || "unknown", refreshErr);
            }
        };
        if (!currentSessionId) {
            await retryWithNewSession();
            return;
        }
        try {
            await fn(currentSessionId);
        }
        catch (err) {
            if (err instanceof AgentSessionsFetchFailedError ||
                err instanceof AgentSessionNotFoundError) {
                await retryWithNewSession();
                return;
            }
            onError(currentSessionId, err);
        }
    }
    return { ...aiSessionId, newSession, withEnsuredSession };
}
/**
 * Parses the storage value for the agent session,
 * returning the id if it exists and is not expired.
 * @param id id of the agent session
 * @returns {Function} parser function
 */
function parseAgentSessionStorageValue(id, { ensureSession } = {}) {
    const isProxyId = id?.includes(":");
    if (!ensureSession && !isProxyId) {
        return () => id;
    }
    return (value) => {
        // If the value is not yet stored, use the provided id as the default value.
        if (value === null) {
            return id;
        }
        try {
            const parsed = JSON.parse(value);
            if (parsed?.expires <= Date.now()) {
                // session is expired - return undefined to trigger new session creation
                return undefined;
            }
            return parsed.id;
        }
        catch (error) {
            console.error("Failed to parse agent session storage value:", error);
            return undefined;
        }
    };
}
/**
 * Sets the storage value for the agent session,
 * by adding an expiry based on the ttl parameter,
 * and stringifying the object.
 * @param id session id
 * @param ttl Time to live in milliseconds.
 * @returns {string} stringified storage value
 */
function stringifyAgentSessionStorageValue(ttl) {
    return (id) => JSON.stringify({ id, expires: Date.now() + ttl });
}
/**
 * Generates the storage key for the given session id
 * @param id id of the agent session
 * @returns {string} storage key
 */
function makeSessionKey(agent, id = "chat:general") {
    const isLegacy = madSDK.ai.isLegacyEndpoint(agent);
    return `session:${agent}:${isLegacy ? "legacy" : "v2"}:${id}:${madSDK.getCurrentUser()?.id}`;
}
useAgentSessionId.makeSessionKey = makeSessionKey;
/**
 * A no-op storage implementation that can be used when `storage` option is set to false.
 * This allows the hook to function without actually persisting the session ID,
 * effectively treating it as in-memory only.
 */
useAgentSessionId.noopStorage = {
    getItem: () => null,
    setItem: () => { },
    removeItem: () => { }
};
//# sourceMappingURL=sessionIds.js.map